// Header: title, week selector (prev/next + week number + ISO date), language toggle, PDF, Email buttons.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';

interface HeaderProps {
  lang: Lang;
  onLangToggle: () => void;
  currentWeek: number | null;
  isoDate: string | null;
  onWeekChange: (week: number) => void;
  code: string;
}

// Format a YYYY-MM-DD date as "D MMM YYYY" in Turkish (e.g. "18 May 2026")
const TR_MONTHS = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
function parseIso(iso: string): Date | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mm, dd] = m;
  // Use UTC to avoid local-tz drift; the iso_date stored is the week's Monday.
  return new Date(Date.UTC(parseInt(y, 10), parseInt(mm, 10) - 1, parseInt(dd, 10)));
}
function fmtDay(d: Date): string {
  return `${d.getUTCDate()} ${TR_MONTHS[d.getUTCMonth()]}`;
}
// Returns the Monday (UTC) of ISO week `weekNum` of year `year`.
// Encoded week key in this app is yyyy * 100 + week, e.g. 202621 → (2026, 21).
function isoWeekMonday(year: number, weekNum: number): Date {
  // Jan 4 is always in ISO week 1 (per ISO 8601).
  const jan4 = new Date(Date.UTC(year, 0, 4));
  // Day of week with Monday=1, Sunday=7
  const jan4Dow = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const target = new Date(week1Monday);
  target.setUTCDate(week1Monday.getUTCDate() + (weekNum - 1) * 7);
  return target;
}
// Given the ISO-week Monday date (or an encoded weekKey yyyy*100+weeknum),
// return a compact range string like "18–24 May 2026"
// (collapses month/year when both endpoints share them).
export function formatWeekRange(input: string | number | null): string {
  if (input == null) return '';
  let monday: Date | null;
  if (typeof input === 'number') {
    const year = Math.floor(input / 100);
    const weekNum = input % 100;
    if (!year || !weekNum) return '';
    monday = isoWeekMonday(year, weekNum);
  } else {
    monday = parseIso(input);
  }
  if (!monday) return typeof input === 'string' ? input : '';
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const sameMonth = monday.getUTCMonth() === sunday.getUTCMonth() && monday.getUTCFullYear() === sunday.getUTCFullYear();
  const sameYear = monday.getUTCFullYear() === sunday.getUTCFullYear();
  if (sameMonth) {
    return `${monday.getUTCDate()}–${sunday.getUTCDate()} ${TR_MONTHS[monday.getUTCMonth()]} ${monday.getUTCFullYear()}`;
  }
  if (sameYear) {
    return `${fmtDay(monday)} – ${fmtDay(sunday)} ${monday.getUTCFullYear()}`;
  }
  return `${fmtDay(monday)} ${monday.getUTCFullYear()} – ${fmtDay(sunday)} ${sunday.getUTCFullYear()}`;
}

function WeekSelector({
  week,
  isoDate,
  onWeekChange,
  lang,
}: {
  week: number | null;
  isoDate: string | null;
  onWeekChange: (week: number) => void;
  lang: Lang;
}) {
  if (!week) return null;

  const year = Math.floor(week / 100);
  const weekNum = week % 100;

  function prevWeek() {
    if (!week) return;
    if (weekNum <= 1) {
      onWeekChange((year - 1) * 100 + 52);
    } else {
      onWeekChange(year * 100 + (weekNum - 1));
    }
  }

  function nextWeek() {
    if (!week) return;
    if (weekNum >= 52) {
      onWeekChange((year + 1) * 100 + 1);
    } else {
      onWeekChange(year * 100 + (weekNum + 1));
    }
  }

  return (
    <div className="flex items-center gap-1 text-sm">
      <button
        onClick={prevWeek}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-neutral-200 text-neutral-600 hover:text-neutral-900 transition-colors"
        aria-label="Önceki hafta"
      >
        ‹
      </button>
      <span className="tabular-nums text-neutral-700 px-2 min-w-40 text-center leading-tight">
        <span className="text-neutral-900 font-medium">Hafta {weekNum}</span>
        {isoDate && (
          <span className="block text-xs text-neutral-500">{formatWeekRange(isoDate)}</span>
        )}
      </span>
      <button
        onClick={nextWeek}
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-neutral-200 text-neutral-600 hover:text-neutral-900 transition-colors"
        aria-label="Sonraki hafta"
      >
        ›
      </button>
    </div>
  );
}

export function Header({ lang, onLangToggle, currentWeek, isoDate, onWeekChange, code }: HeaderProps) {
  const location = useLocation();

  const navLinks = [
    { to: '/', label: t('radar', lang) },
    { to: '/pricing', label: t('pricing', lang) },
    { to: '/benchmark', label: t('benchmark', lang) },
    { to: '/price-changes', label: t('priceChanges', lang) },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-neutral-200 bg-neutral-50/95 backdrop-blur-sm no-print">
      {/* Confidentiality bar — visible on every page and in print/PDF. */}
      <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-center text-[11px] tracking-wide py-1 px-4">
        {t('confidentialBanner', lang)}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 gap-4">
          {/* Logo + title */}
          <div className="flex items-center gap-3 shrink-0">
            <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
              <circle cx="12" cy="12" r="5" stroke="#34d399" strokeWidth="1.5" />
              <circle cx="12" cy="12" r="2" fill="#34d399" />
              <line x1="12" y1="2" x2="12" y2="7" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span className="text-xs font-semibold tracking-widest text-neutral-900 uppercase">
              {t('appTitle', lang)}
            </span>
          </div>

          {/* Nav */}
          <nav className="hidden sm:flex items-center gap-0.5">
            {navLinks.map(({ to, label }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={`${to}?code=${code}`}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors
                    ${active
                      ? 'bg-neutral-200 text-neutral-900'
                      : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-200/50'
                    }`}
                >
                  {label}
                </Link>
              );
            })}
          </nav>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <WeekSelector
              week={currentWeek}
              isoDate={isoDate}
              onWeekChange={onWeekChange}
              lang={lang}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
